import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, 
  Users, 
  GraduationCap, 
  MapPin, 
  Calendar, 
  FileText, 
  Activity, 
  Settings,
  Award,
  Download,
  Shield,
  HelpCircle,
  School,
  UserCircle,
  Heart,
  CheckCircle,
  XCircle,
  Clock,
  Target,
  TrendingUp,
  Trophy,
  ShieldCheck,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Waves,
  Phone,
  Mail,
  Camera,
} from 'lucide-react';

const ManualSection = ({ icon: Icon, title, children, defaultOpen = true }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Card className="mb-6" data-testid={`manual-section-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardHeader 
        className="cursor-pointer hover:bg-slate-50 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
              <Icon className="w-5 h-5" />
            </div>
            {title}
          </span>
          {isOpen ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />}
        </CardTitle>
      </CardHeader>
      {isOpen && (
        <CardContent className="prose prose-slate max-w-none">
          {children}
        </CardContent>
      )}
    </Card>
  );
};

const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button 
        className="w-full py-4 flex items-center justify-between text-left hover:text-primary transition-colors"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-medium text-slate-900">{question}</span>
        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {isOpen && (
        <div className="pb-4 text-slate-600">{answer}</div>
      )}
    </div>
  );
};

const Manual = () => {
  return (
    <div className="space-y-6 animate-fade-in max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">User Manual</h1>
        <p className="text-slate-500">Complete guide to using the Eswatini Seals Swim Academy Management System</p>
      </div>

      {/* Platform Overview */}
      <Card className="bg-gradient-to-r from-primary/5 to-teal-50 border-primary/20">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-bold text-primary mb-4 flex items-center gap-2">
            <Waves className="w-6 h-6" />
            About This Platform
          </h2>
          <p className="text-slate-700 mb-4">
            Welcome to the Eswatini Seals Swim Academy Management System. This platform is designed to help our community:
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Waves className="w-4 h-4 text-blue-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Learn-to-Swim</h4>
                <p className="text-sm text-slate-600">Professional swim instruction for all ages</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
              <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Drowning Prevention</h4>
                <p className="text-sm text-slate-600">Water safety education and awareness</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
              <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <School className="w-4 h-4 text-emerald-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Schools Program</h4>
                <p className="text-sm text-slate-600">Group instruction for educational institutions</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-white rounded-lg">
              <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                <Trophy className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Digital Certificates</h4>
                <p className="text-sm text-slate-600">Automatic completion certificates</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Roles - UPDATED */}
      <ManualSection icon={Shield} title="User Roles & Permissions">
        <div className="space-y-6">
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-900 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Admin
            </h4>
            <p className="text-blue-800 mt-1">Full system access including:</p>
            <ul className="list-disc list-inside text-blue-700 ml-4 mt-2 space-y-1">
              <li>Create, edit, and delete students, coaches, programs, and locations</li>
              <li>Approve and manage certificates</li>
              <li>Manage school registrations</li>
              <li>Access academy settings and branding</li>
              <li>Generate and export reports</li>
              <li>Cancel or reschedule lessons</li>
              <li>View activity logs</li>
            </ul>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-900 flex items-center gap-2">
              <GraduationCap className="w-4 h-4" /> Coach
            </h4>
            <p className="text-amber-800 mt-1">Limited access for instruction:</p>
            <div className="grid sm:grid-cols-2 gap-4 mt-2">
              <div>
                <p className="font-medium text-emerald-700 flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Coaches CAN:
                </p>
                <ul className="list-disc list-inside text-emerald-600 ml-4 text-sm space-y-1">
                  <li>View all students</li>
                  <li>Mark lesson attendance</li>
                  <li>Update skill level & progress notes</li>
                  <li>View schedules and locations</li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-red-700 flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> Coaches CANNOT:
                </p>
                <ul className="list-disc list-inside text-red-600 ml-4 text-sm space-y-1">
                  <li>Add new students</li>
                  <li>Edit student personal details</li>
                  <li>Delete any records</li>
                  <li>Change schedules or locations</li>
                </ul>
              </div>
            </div>
            <p className="text-amber-700 text-sm mt-3 italic">
              Why? This ensures data integrity and protects sensitive student information.
            </p>
          </div>
          
          <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
            <h4 className="font-semibold text-purple-900 flex items-center gap-2">
              <Heart className="w-4 h-4" /> Parent
            </h4>
            <p className="text-purple-800 mt-1">View their child's progress:</p>
            <ul className="list-disc list-inside text-purple-700 ml-4 mt-2 space-y-1">
              <li>View enrolled children's profiles</li>
              <li>See progress, skill milestones, and attendance</li>
              <li>View upcoming lesson schedules</li>
              <li>Download completion certificates</li>
            </ul>
          </div>
          
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-semibold text-slate-900 flex items-center gap-2">
              <UserCircle className="w-4 h-4" /> Adult Learner
            </h4>
            <p className="text-slate-700 mt-1">View own progress:</p>
            <ul className="list-disc list-inside text-slate-600 ml-4 mt-2 space-y-1">
              <li>View personal profile and enrollment</li>
              <li>Track lesson progress and milestones</li>
              <li>Download completion certificate</li>
            </ul>
          </div>
          
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <h4 className="font-semibold text-emerald-900 flex items-center gap-2">
              <School className="w-4 h-4" /> School Administrator
            </h4>
            <p className="text-emerald-800 mt-1">Manage school enrollment:</p>
            <ul className="list-disc list-inside text-emerald-700 ml-4 mt-2 space-y-1">
              <li>Register school for group lessons</li>
              <li>View program pricing and discounts</li>
              <li>Receive confirmation emails</li>
            </ul>
          </div>
        </div>
      </ManualSection>

      {/* Registration Flows - NEW SECTION */}
      <ManualSection icon={UserCircle} title="Registration Flows">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <Heart className="w-4 h-4 text-pink-500" /> Parent/Child Registration
            </h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-2">
              <li>Admin selects <Badge variant="outline" className="text-xs">Child</Badge> as Student Type</li>
              <li>Enter child's personal information:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>First Name and Last Name (required)</li>
                  <li>Date of Birth (required - age auto-calculated)</li>
                  <li>Sex (optional)</li>
                  <li>Grade/Class (optional)</li>
                </ul>
              </li>
              <li>Enter guardian information:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>Guardian Name (required)</li>
                  <li>Guardian Phone (required)</li>
                  <li>Relationship (Mother, Father, Guardian, Other)</li>
                  <li>Guardian Email (optional)</li>
                </ul>
              </li>
              <li>Select Program and Location</li>
              <li>Optional: Upload passport photo</li>
              <li>Set Start Date (enrollment date is set automatically)</li>
            </ol>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <UserCircle className="w-4 h-4 text-blue-500" /> Adult Registration
            </h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-2">
              <li>Admin selects <Badge variant="outline" className="text-xs">Adult</Badge> as Student Type</li>
              <li>Enter personal information:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>First Name and Last Name (required)</li>
                  <li>Date of Birth (required - age auto-calculated)</li>
                  <li>Sex (optional)</li>
                </ul>
              </li>
              <li>Enter contact information:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>Email (required)</li>
                  <li>Phone (required)</li>
                </ul>
              </li>
              <li>Select Program and Location</li>
              <li>Optional: Upload passport photo</li>
            </ol>
            <p className="text-slate-500 text-sm mt-2 italic">
              Note: Adult students do not require guardian information.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 flex items-center gap-2 mb-3">
              <School className="w-4 h-4 text-emerald-500" /> School Registration (Online)
            </h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-2">
              <li>Visit the Schools Program page from the landing page</li>
              <li>Fill in school details:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>School Name and Type (Public, Private, Other)</li>
                  <li>Physical Address and Region</li>
                </ul>
              </li>
              <li>Provide contact person information:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>Contact Name and Role (Principal, Administrator, etc.)</li>
                  <li>Email and Phone</li>
                </ul>
              </li>
              <li>Specify program requirements:
                <ul className="list-disc list-inside ml-6 text-sm mt-1 space-y-1">
                  <li>Number of Students</li>
                  <li>Grade Levels</li>
                  <li>Preferred Session Date and Time</li>
                </ul>
              </li>
              <li>Accept Terms and Privacy Policy</li>
              <li>Submit registration</li>
            </ol>
            <p className="text-emerald-600 text-sm mt-2">
              ✓ Bulk discount: 10% off for 20+ students
            </p>
          </div>
        </div>
      </ManualSection>

      {/* Student Management - UPDATED */}
      <ManualSection icon={Users} title="Student Management">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Adding a New Student</h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-1">
              <li>Click the "Add Student" button (Admin/Manager only)</li>
              <li>First, select Student Type: <Badge variant="outline" className="text-xs">Child</Badge> or <Badge variant="outline" className="text-xs">Adult</Badge></li>
              <li>Fill in the required fields based on type</li>
              <li>Select a Program and Location</li>
              <li>Click "Create Student"</li>
            </ol>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Editing a Student</h4>
            <p className="text-slate-600 mb-2">Click on any student to view their profile, then click "Edit".</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="p-3 bg-emerald-50 rounded-lg">
                <p className="font-medium text-emerald-700 text-sm">Can be changed:</p>
                <ul className="list-disc list-inside text-emerald-600 text-sm space-y-1">
                  <li>Name, Date of Birth</li>
                  <li>Contact/Guardian info</li>
                  <li>Program and Location</li>
                  <li>Status (active, inactive, etc.)</li>
                  <li>Start Date</li>
                </ul>
              </div>
              <div className="p-3 bg-red-50 rounded-lg">
                <p className="font-medium text-red-700 text-sm">Cannot be changed:</p>
                <ul className="list-disc list-inside text-red-600 text-sm space-y-1">
                  <li>Student Type (Child/Adult)</li>
                  <li>Enrollment Date</li>
                  <li>Age (auto-calculated)</li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Important: Age Calculation
            </h4>
            <p className="text-amber-700 mt-2">
              Age is <strong>automatically calculated</strong> from Date of Birth. You cannot manually enter or edit age.
              This ensures accuracy and prevents data entry errors.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Child vs Adult Students</h4>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 text-slate-500">Field</th>
                  <th className="text-center py-2 text-slate-500">Child</th>
                  <th className="text-center py-2 text-slate-500">Adult</th>
                </tr>
              </thead>
              <tbody className="text-slate-600">
                <tr className="border-b">
                  <td className="py-2">Guardian Name</td>
                  <td className="text-center">Required</td>
                  <td className="text-center text-slate-400">Not shown</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Guardian Phone</td>
                  <td className="text-center">Required</td>
                  <td className="text-center text-slate-400">Not shown</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Email</td>
                  <td className="text-center text-slate-400">Optional (guardian)</td>
                  <td className="text-center">Required</td>
                </tr>
                <tr className="border-b">
                  <td className="py-2">Phone</td>
                  <td className="text-center text-slate-400">Not shown</td>
                  <td className="text-center">Required</td>
                </tr>
                <tr>
                  <td className="py-2">Grade/Class</td>
                  <td className="text-center">Optional</td>
                  <td className="text-center text-slate-400">Not shown</td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Enrollment Date vs Start Date</h4>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li><strong>Enrollment Date:</strong> Set automatically when student is created. Cannot be changed.</li>
              <li><strong>Start Date:</strong> The actual date lessons begin. Can be edited by admin.</li>
            </ul>
          </div>
        </div>
      </ManualSection>

      {/* Lessons & Attendance - UPDATED */}
      <ManualSection icon={Calendar} title="Lessons & Attendance">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Lesson Status Types</h4>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-emerald-600" />
                <div>
                  <p className="font-medium text-emerald-700">Completed</p>
                  <p className="text-sm text-emerald-600">Lesson attended successfully</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
                <div>
                  <p className="font-medium text-amber-700">Scheduled</p>
                  <p className="text-sm text-amber-600">Upcoming lesson</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
                <XCircle className="w-5 h-5 text-red-500" />
                <div>
                  <p className="font-medium text-red-700">Missed</p>
                  <p className="text-sm text-red-600">Student did not attend</p>
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <Clock className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="font-medium text-slate-700">Remaining</p>
                  <p className="text-sm text-slate-500">Lessons left in program</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Marking Attendance</h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-1">
              <li>Go to the student's detail page</li>
              <li>Find the lesson in the "Lesson History" section</li>
              <li>Click the "Complete" button next to a scheduled lesson</li>
              <li>The lesson status will change to "Completed"</li>
            </ol>
            <p className="text-slate-500 text-sm mt-2">
              Both Admins and Coaches can mark attendance.
            </p>
          </div>
          
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="font-semibold text-blue-800">Cancel or Reschedule Lessons</h4>
            <p className="text-blue-700 mt-2">
              Only <strong>Admins</strong> can cancel or reschedule lessons. Coaches and parents cannot modify the schedule.
              Contact your administrator if a lesson needs to be changed.
            </p>
          </div>
        </div>
      </ManualSection>

      {/* Progress & Certificates - UPDATED */}
      <ManualSection icon={Award} title="Progress & Certificates">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Skill Milestones</h4>
            <p className="text-slate-600 mb-3">
              Students progress through 6 skill milestones as they complete lessons:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {[
                { name: 'Water Comfort', threshold: '10%' },
                { name: 'Basic Floating', threshold: '25%' },
                { name: 'Kicking & Movement', threshold: '40%' },
                { name: 'Arm Strokes', threshold: '60%' },
                { name: 'Full Stroke', threshold: '80%' },
                { name: 'Swim Ready', threshold: '100%' },
              ].map((milestone, idx) => (
                <div key={idx} className="p-2 bg-slate-50 rounded-lg text-sm">
                  <p className="font-medium text-slate-700">{milestone.name}</p>
                  <p className="text-slate-500 text-xs">{milestone.threshold} progress</p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-3">
              Milestones are shown with checkmarks when achieved.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Attendance Percentage</h4>
            <p className="text-slate-600">
              The attendance rate is calculated as:
            </p>
            <div className="bg-slate-100 rounded-lg p-3 mt-2 font-mono text-sm">
              Attendance % = (Completed Lessons ÷ (Completed + Missed)) × 100
            </div>
            <p className="text-slate-500 text-sm mt-2">
              Scheduled lessons are not counted until they are completed or missed.
            </p>
          </div>
          
          <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2">
              <Trophy className="w-5 h-5" /> Certificate Unlocked!
            </h4>
            <p className="text-amber-700 mt-2">
              When a student completes <strong>100%</strong> of their program lessons, their certificate becomes available.
            </p>
            <ol className="list-decimal list-inside text-amber-700 mt-3 space-y-1">
              <li>Certificate status changes to "In Progress" (awaiting approval)</li>
              <li>Admin reviews and approves the certificate</li>
              <li>Status changes to "Certificate Unlocked! 🎉"</li>
              <li>Student/Parent can download the PDF certificate</li>
            </ol>
            <p className="text-amber-600 text-sm mt-3 italic">
              Certificates are automatic upon completion - no request needed.
            </p>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Downloading Your Certificate</h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-1">
              <li>Go to the student's detail page</li>
              <li>Look for the golden "Certificate Unlocked!" section</li>
              <li>Click the "Download Certificate" button</li>
              <li>The PDF will download to your device</li>
            </ol>
          </div>
        </div>
      </ManualSection>

      {/* Schools Program - NEW SECTION */}
      <ManualSection icon={School} title="Schools Program">
        <div className="space-y-6">
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">How Schools Register</h4>
            <ol className="list-decimal list-inside text-slate-600 space-y-1">
              <li>Visit the landing page and click "Schools Program"</li>
              <li>Fill in school and contact information</li>
              <li>Specify number of students and grade levels</li>
              <li>Choose preferred dates and times</li>
              <li>Submit the registration form</li>
              <li>Receive confirmation email</li>
            </ol>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">What Schools Receive</h4>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li><strong>Certified Instructors:</strong> Professional coaches with safety training</li>
              <li><strong>Structured Curriculum:</strong> Age-appropriate lesson plans</li>
              <li><strong>Flexible Scheduling:</strong> Sessions designed around school timetables</li>
              <li><strong>On-site or Off-site:</strong> Lessons at your school's pool or our facilities</li>
              <li><strong>Bulk Discounts:</strong> 10% off for groups of 20+ students</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">What Parents Receive</h4>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li><strong>Progress Tracking:</strong> Digital record of child's swimming progress</li>
              <li><strong>Completion Certificates:</strong> Official certificates upon program completion</li>
              <li><strong>Safety Assurance:</strong> Certified coaches and safety-first approach</li>
              <li><strong>Next Lesson Info:</strong> Clear information about upcoming sessions</li>
            </ul>
          </div>
          
          <div>
            <h4 className="font-semibold text-slate-900 mb-3">Admin Management</h4>
            <p className="text-slate-600 mb-2">Admins can manage school registrations at /school-registrations:</p>
            <ul className="list-disc list-inside text-slate-600 space-y-1">
              <li>View all registration requests</li>
              <li>Update status (Pending, Contacted, Confirmed, Cancelled)</li>
              <li>Add admin notes for internal tracking</li>
              <li>Search and filter by school name, region, or status</li>
              <li>Export all registrations to CSV</li>
            </ul>
          </div>
          
          <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
            <h4 className="font-semibold text-emerald-800">Pricing</h4>
            <p className="text-emerald-700 mt-2">
              Base price: <strong>E150 per student</strong>
            </p>
            <p className="text-emerald-600 mt-1">
              Groups of 20+ students receive <strong>10% bulk discount</strong>
            </p>
          </div>
        </div>
      </ManualSection>

      {/* Dashboard & Navigation - UPDATED */}
      <ManualSection icon={BookOpen} title="Dashboard & Navigation">
        <div className="space-y-4">
          <p className="text-slate-600">
            The dashboard provides an overview of your academy's key metrics:
          </p>
          <ul className="list-disc list-inside text-slate-600 space-y-1">
            <li><strong>Total Students:</strong> Click to see all enrolled students</li>
            <li><strong>Lessons Today:</strong> Scheduled lessons for today</li>
            <li><strong>Completed Programs:</strong> Students who finished their program</li>
            <li><strong>Upcoming Lessons:</strong> Future scheduled lessons</li>
          </ul>
          
          <div>
            <h4 className="font-semibold text-slate-900 mt-4 mb-2">Program Cards</h4>
            <p className="text-slate-600">
              The landing page displays program cards with consistent styling:
            </p>
            <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
              <li><strong>Saturday Program:</strong> Weekend sessions</li>
              <li><strong>Holiday Program:</strong> School holiday intensives</li>
              <li><strong>Schools Program:</strong> Group lessons for educational institutions</li>
            </ul>
            <p className="text-slate-500 text-sm mt-2">
              All cards now have identical layout and styling.
            </p>
          </div>
        </div>
      </ManualSection>

      {/* Settings - Keep existing but update */}
      <ManualSection icon={Settings} title="Settings (Admin Only)">
        <p className="text-slate-600">
          Customize academy branding and information:
        </p>
        <ul className="list-disc list-inside text-slate-600 mt-2 space-y-1">
          <li><strong>Logo:</strong> Upload your academy logo</li>
          <li><strong>Academy Name:</strong> Displayed throughout the app</li>
          <li><strong>About Text:</strong> Shown on the About page</li>
          <li><strong>Digital Signature:</strong> Appears on certificates</li>
          <li><strong>Gallery:</strong> Add images for the gallery section</li>
          <li><strong>Contact Info:</strong> Email, phone, and address</li>
        </ul>
        <p className="text-slate-500 text-sm mt-3">
          Schools Program settings are managed separately at /school-registrations.
        </p>
      </ManualSection>

      {/* FAQ - NEW SECTION */}
      <ManualSection icon={HelpCircle} title="Frequently Asked Questions">
        <div className="space-y-0">
          <FAQItem 
            question="Will my data be lost after system updates?"
            answer="No. All your data (students, lessons, certificates) is safely stored and preserved during updates. Updates only change how the system looks and works, not your data."
          />
          <FAQItem 
            question="Why can't coaches add or edit students?"
            answer="This is a safety and data integrity measure. Only administrators can add or modify student records to ensure accuracy and protect sensitive personal information."
          />
          <FAQItem 
            question="Why can't I edit a student's age?"
            answer="Age is automatically calculated from the Date of Birth for accuracy. This prevents data entry errors and ensures the age is always current. To change age, update the Date of Birth field."
          />
          <FAQItem 
            question="Why is my certificate locked?"
            answer="Certificates become available only when a student completes 100% of their program lessons. Once complete, an admin must approve the certificate before it can be downloaded."
          />
          <FAQItem 
            question="Why can't I change a student from Child to Adult?"
            answer="Student Type is locked after creation because it affects what information is required (guardian info for children, direct contact for adults). Create a new student record if needed."
          />
          <FAQItem 
            question="How do I cancel or reschedule a lesson?"
            answer="Only administrators can cancel or reschedule lessons. Contact your academy administrator to request changes to the lesson schedule."
          />
          <FAQItem 
            question="What's the difference between Enrollment Date and Start Date?"
            answer="Enrollment Date is when the student was registered in the system (cannot be changed). Start Date is when lessons actually begin (can be edited by admin)."
          />
          <FAQItem 
            question="How does the Schools Program discount work?"
            answer="Schools registering 20 or more students receive an automatic 10% discount. The discount is calculated and shown on the registration form."
          />
        </div>
      </ManualSection>

      {/* Tips */}
      <Card className="bg-primary/5 border-primary/20">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-bold text-primary mb-3 flex items-center gap-2">
            <HelpCircle className="w-5 h-5" />
            Tips for Parents
          </h2>
          <ul className="list-disc list-inside space-y-2 text-slate-700">
            <li><strong>Check Next Lesson:</strong> The student detail page shows your child's next scheduled lesson with date, time, location, and coach</li>
            <li><strong>What to Bring:</strong> Swimsuit, towel, and goggles for each lesson</li>
            <li><strong>Track Progress:</strong> Monitor skill milestones and attendance percentage</li>
            <li><strong>Download Certificate:</strong> Once your child completes the program, download their official certificate</li>
            <li><strong>Safety First:</strong> All our coaches are certified with safety-first training</li>
          </ul>
        </CardContent>
      </Card>

      {/* Contact Support */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="p-6">
          <h2 className="font-heading text-xl font-bold text-slate-800 mb-3 flex items-center gap-2">
            <Phone className="w-5 h-5" />
            Need Help?
          </h2>
          <p className="text-slate-600 mb-4">
            If you have questions not covered in this manual, please contact us:
          </p>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center gap-2 text-slate-700">
              <Mail className="w-4 h-4 text-primary" />
              <span>info@eswatiniseals.com</span>
            </div>
            <div className="flex items-center gap-2 text-slate-700">
              <Phone className="w-4 h-4 text-primary" />
              <span>+268 2404 1234</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Manual;
